// ═══════════════════════════════════════════════════════════════════════════════
// Person name input with family member suggestion chips
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import type { FamilyMemberListItem } from '@fdh/shared';
import { familyMemberApi } from '../../lib/api';

type PersonNameSuggestionInputProps = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  suggestHeading: string;
};

const toSuggestionNames = (members: FamilyMemberListItem[]): string[] => {
  const names = members
    .map((member) => member.nickname.trim() || member.name.trim())
    .filter((name) => name !== '');
  return [...new Set(names)];
};

export const PersonNameSuggestionInput = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  suggestHeading,
}: PersonNameSuggestionInputProps) => {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [familyLoaded, setFamilyLoaded] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const res = await familyMemberApi.list();
        if (res.success && Array.isArray(res.data) && res.data.length > 0) {
          setSuggestions(toSuggestionNames(res.data));
          return;
        }
        setSuggestions([]);
      } finally {
        setFamilyLoaded(true);
      }
    })();
  }, []);

  const showFamilySuggestions = familyLoaded && suggestions.length > 0;

  const handleKeyDown = (callback: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  };

  return (
    <div>
      <label className="text-sm font-medium text-gray-700 dark:text-gray-200" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={120}
        autoComplete="off"
        className="mt-1 w-full min-h-11 rounded-lg border border-gray-300 px-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
      />
      {showFamilySuggestions ? (
        <div className="mt-3">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{suggestHeading}</p>
          <div className="mt-1 flex flex-wrap gap-2">
            {suggestions.map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => onChange(name)}
                onKeyDown={handleKeyDown(() => onChange(name))}
                tabIndex={0}
                className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                {name}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};
